import React, { useContext, useMemo } from "react";
import { Container, Row, Col, Tab, Nav, Accordion } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "@assets/css/components/DashboardFAQ/style.css";
import DynamicBreadcrumb from "@components/general/DynamicBreadcrumb/DynamicBreadcrumb";
import TitleDashboard from "@components/general/Titles/TitleDashboard";
import { Animated } from "react-animated-css";
import { Link } from "react-scroll";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUp } from "@fortawesome/free-solid-svg-icons";
import typesTiposUsuario from "@helpers/types/typesTiposUsuario";
import UserContext from "@context/User/UserContext";

export const DashboardFAQ = ({ showScrollButton }) => {
  const { contextValue } = useContext(UserContext);
  const { usuarioLogeado } = contextValue;
  const { rol } = usuarioLogeado;

  const { t } = useTranslation();

  // 🔥 Helper para renderizar respuestas dinámicas
  const renderAnswer = (answer) => {
    if (typeof answer === "string") {
      return <p dangerouslySetInnerHTML={{ __html: answer }} />;
    }

    if (typeof answer === "object") {
      return Object.keys(answer).map((key) => (
        <p key={key} dangerouslySetInnerHTML={{ __html: answer[key] }} />
      ));
    }

    return null;
  };

  // 🔥 Nav dinámico desde JSON
  const navItems = useMemo(() => {
    return [
      {
        eventKey: "platform",
        title: t("dashboardFAQ.navigation.platform"),
        permittedTo: Object.values(typesTiposUsuario),
      },
      {
        eventKey: "groups",
        title: t("dashboardFAQ.navigation.groups"),
        permittedTo: Object.values(typesTiposUsuario),
      },
      {
        eventKey: "certifications",
        title: t("dashboardFAQ.navigation.certifications"),
        permittedTo: Object.values(typesTiposUsuario),
      },
      {
        eventKey: "consulting",
        title: t("dashboardFAQ.navigation.consulting"),
        permittedTo: Object.values(typesTiposUsuario),
      },
      {
        eventKey: "others",
        title: t("dashboardFAQ.navigation.others"),
        permittedTo: Object.values(typesTiposUsuario),
      },
    ];
  }, [t]);

  // 🔥 Generar secciones dinámicamente
  const tabPaneItems = useMemo(() => {
    const sections = ["platform", "groups", "certifications", "consulting", "others"];

    return sections.reduce((acc, sectionKey) => {
      const questions = t(`dashboardFAQ.sections.${sectionKey}`, {
        returnObjects: true,
      });

      if (!questions) return acc;

      acc[sectionKey] = Object.keys(questions).map((qKey) => {
        const question = t(
          `dashboardFAQ.sections.${sectionKey}.${qKey}.question`
        );

        const answer = t(
          `dashboardFAQ.sections.${sectionKey}.${qKey}.answer`,
          { returnObjects: true }
        );

        return {
          headerText: question,
          content: renderAnswer(answer),
          permittedTo: Object.values(typesTiposUsuario), // puedes ajustar esto
        };
      });

      return acc;
    }, {});
  }, [t]);

  return (
    <Container fluid className="p-0 mb-3">
      {showScrollButton && (
        <Animated
          animationIn="fadeIn"
          isVisible={true}
          style={{ zIndex: "9999", position: "absolute" }}
        >
          <span
            className="rounded-circle scrollTop text-center d-flex"
            style={{
              height: "50px",
              width: "50px",
              fontSize: "2rem",
              backgroundColor: "#F59E0B",
              position: "fixed",
              bottom: "70px",
              right: "40px",
            }}
          >
            <Link
              className="pointer"
              to="scrollTopElement"
              spy={true}
              smooth={true}
              duration={800}
              containerId="scrollTop"
              style={{ color: "black", width: "100%" }}
            >
              <FontAwesomeIcon icon={faArrowUp} />
            </Link>
          </span>
        </Animated>
      )}

      {/* Breadcrumb */}
      <DynamicBreadcrumb
        arrayBreadcrumbs={[
          {
            title: t("dashboardFAQ.breadcrumb.title"),
            onClick: () => {},
          },
        ]}
      />

      {/* Title */}
      <Row className="mb-5">
        <Col>
          <TitleDashboard title={t("dashboardFAQ.pageTitle")} />
        </Col>
      </Row>

      {/* Tabs */}
      <Row>
        <Col>
          <Tab.Container defaultActiveKey="platform">
            <Container fluid>
              <Row>
                {/* NAV */}
                <Col sm={4}>
                  <Nav variant="pills" className="flex-column">
                    {navItems.map(
                      (navItem, idx) =>
                        navItem.permittedTo.includes(rol) && (
                          <Nav.Item key={idx}>
                            <Nav.Link eventKey={navItem.eventKey}>
                              {navItem.title}
                            </Nav.Link>
                          </Nav.Item>
                        )
                    )}
                  </Nav>
                </Col>

                {/* CONTENT */}
                <Col sm={8}>
                  <Tab.Content>
                    {navItems.map(({ eventKey }, idx) => (
                      <Tab.Pane eventKey={eventKey} key={idx}>
                        <Accordion defaultActiveKey="0">
                          {tabPaneItems[eventKey]?.map((item, i) => (
                            item.permittedTo.includes(rol) && (
                              <Accordion.Item eventKey={i.toString()} key={i}>
                                <Accordion.Header>
                                  <h6>{item.headerText}</h6>
                                </Accordion.Header>
                                <Accordion.Body>
                                  {item.content}
                                </Accordion.Body>
                              </Accordion.Item>
                            )
                          ))}
                        </Accordion>
                      </Tab.Pane>
                    ))}
                  </Tab.Content>
                </Col>
              </Row>
            </Container>
          </Tab.Container>
        </Col>
      </Row>
    </Container>
  );
};